// PopularCommunityCard.tsx — cover-art card for the Popular grid.
import { type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ConsolePanel, Button } from '@/ui';
import { useCommunities, useRequireAuth } from '@/hooks';
import { CommunityIcon } from './CommunityIcon';
import { coverBackground } from './meta';
import { isHttpUrl } from '@/core/lib/isHttpUrl';
import type { Community } from '@/core/models/community';

const Card = styled(ConsolePanel)`
  padding: 0; overflow: hidden; display: flex; flex-direction: column;
  text-decoration: none;
`;
const Cover = styled.div<{ $tone?: string; $image?: string }>`
  height: 84px;
  background: ${({ $tone, $image }) => $image ? `center / cover no-repeat url("${String($image).replace(/["\\]/g, '')}")` : coverBackground($tone)};
  display: flex; align-items: center; justify-content: center;
`;
const Body = styled.div`padding: 12px; display: flex; flex-direction: column; gap: 8px;`;
const Name = styled.span`
  font-family: ${({ theme }) => theme.font.display}; font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textPrimary};
`;
const Members = styled.span`
  font-family: ${({ theme }) => theme.font.mono}; font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;
const Row = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 8px;`;
const CardLink = styled(Link)`text-decoration: none;`;

export function PopularCommunityCard({ community }: { community: Community }) {
  const { toggleJoin } = useCommunities();
  const requireAuth = useRequireAuth();
  const coverIsUrl = isHttpUrl(community.coverTone);

  function handleJoin(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!requireAuth(`join ${community.name}`)) return;
    void toggleJoin(community.id);
  }

  return (
    <CardLink to={`/communities/${community.id}`} aria-label={`Open ${community.name}`}>
      <Card>
        <Cover
          $tone={coverIsUrl ? undefined : community.coverTone}
          $image={coverIsUrl ? community.coverTone : undefined}
        >
          <CommunityIcon svg={community.icon} size={48} />
        </Cover>
        <Body>
          <Name>{community.name}</Name>
          <Members>{community.members}</Members>
          <Row>
            <Members>{community.postsCount} posts</Members>
            <Button
              variant={community.joined ? 'secondary' : 'outline'}
              size="sm"
              onClick={handleJoin}
              aria-label={`${community.joined ? 'Leave' : 'Join'} ${community.name}`}
            >
              {community.joined ? 'Joined' : 'Join'}
            </Button>
          </Row>
        </Body>
      </Card>
    </CardLink>
  );
}
